import React from 'react';
import { Box, Text, Flex, Heading, Divider, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerHeader, DrawerBody, Spinner } from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';

function cleanStackTrace(stackTrace) {
  if (!stackTrace) return '';
  return stackTrace
    .split('\n')
    .filter(line => {
      const trimmed = line.trim();
      if (trimmed.startsWith('<![CDATA[  [chromium]')) return false;
      if (trimmed.startsWith('attachment #')) return false;
      if (trimmed.startsWith('Usage:')) return false;
      if (trimmed.match(/^out\/.+\.zip$/)) return false;
      if (trimmed === '────────────────────────────────────────────────────────────────────────────────────────────────') return false;
      return true;
    })
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function TestCaseDrawer({ open, onClose, testcase, loading, aiSummary }) {
  // Try multiple possible field names for error data
  const errorMsg = testcase?.errorMessage || testcase?.error || testcase?.message || testcase?.failureMessage || 'No error message available.';
  const stackTrace = testcase?.errorStack || testcase?.stackTrace || testcase?.stack || testcase?.trace || '';
  const cleanedStack = cleanStackTrace(stackTrace);
  
  // Debug logging removed for production

  return (
    <Drawer isOpen={open} onClose={onClose} placement={{ base: 'bottom', md: 'right' }} size={{ base: 'full', md: 'md' }}>
      <DrawerOverlay />
      <DrawerContent maxW={{ base: '100vw', md: '700px' }}>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" bg="white">
          {loading ? <Box as="span" bg="gray.100" w="120px" h="24px" borderRadius="md" /> : (testcase?.title || 'Test Case Details')}
        </DrawerHeader>
        <DrawerBody p={4} pt={2}>
          <Heading size="sm" mb={2} color="red.600" fontWeight="semibold">Error Message</Heading>
          <Box mb={6} bg="red.50" color="red.800" borderRadius="md" p={4} fontWeight="bold" fontSize="md" boxShadow="md" letterSpacing="tight">
            {errorMsg}
          </Box>
          <Divider mb={6} />
          <Heading size="sm" mb={2} color="gray.700" fontWeight="semibold">Stack Trace</Heading>
          {cleanedStack ? (
            <Box bg="gray.100" color="gray.800" borderRadius="md" p={0} fontSize="sm" maxH="700px" overflowY="auto" mb={6} fontFamily="mono" boxShadow="sm" border="1px solid #E2E8F0">
              <ReactMarkdown
                children={`\`\`\`text\n${cleanedStack}\n\`\`\``}
                rehypePlugins={[rehypeHighlight]}
                components={{
                  code: ({node, ...props}) => <Box as="code" bg="gray.100" color="gray.800" px={1} py={0.5} borderRadius="sm" fontSize="sm" fontFamily="mono" {...props} />, 
                  pre: ({node, ...props}) => <Box as="pre" bg="gray.100" color="gray.800" borderRadius="md" p={3} fontSize="sm" fontFamily="mono" whiteSpace="pre-wrap" textAlign="left" {...props} />,
                }}
              />
            </Box>
          ) : (
            <Text color="gray.400" mb={6}>No stack trace available.</Text>
          )}
          <Divider mb={6} />
          {loading ? (
            <Flex align="center" justify="center" minH="60px" mb={4}>
              <Spinner size="md" color="blue.500" thickness="4px" speed="0.7s" />
              <Text ml={3} color="blue.700">Loading AI summary...</Text>
            </Flex>
          ) : aiSummary ? (
            <Box mt={0} bg="blue.50" color="blue.900" borderRadius="md" p={4} fontSize="sm" boxShadow="sm" border="1px solid #90cdf4">
              <Heading size="sm" mb={2} color="blue.700" fontWeight="semibold">AI Failure Summary</Heading>
              {typeof aiSummary === 'string' ? (
                <ReactMarkdown
                  children={aiSummary}
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeHighlight]}
                  components={{
                    code: ({node, ...props}) => <Box as="code" bg="gray.100" color="blue.800" px={1} py={0.5} borderRadius="sm" fontSize="sm" fontFamily="mono" {...props} />,
                    li: ({node, ...props}) => <Box as="li" ml={4} mb={1} {...props} />,
                    pre: ({node, ...props}) => <Box as="pre" bg="gray.100" color="blue.900" borderRadius="md" p={2} fontSize="sm" fontFamily="mono" mb={2} {...props} />,
                  }}
                />
              ) : (
                <Text color="red.600" fontSize="sm">
                  Invalid AI summary format. Expected string but received: {typeof aiSummary}
                </Text>
              )}
            </Box>
          ) : (
            <Box mt={0} bg="gray.50" color="gray.600" borderRadius="md" p={4} fontSize="sm" boxShadow="sm" border="1px solid #e2e8f0">
              <Text fontSize="sm">No AI analysis available for this test case.</Text>
            </Box>
          )}
        </DrawerBody>
        <Divider />
        <Flex p={3} justify="flex-end">
          {/* Placeholder for future actions */}
        </Flex>
      </DrawerContent>
    </Drawer>
  );
}

export default TestCaseDrawer; 